import { Component, Input, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges } from '@angular/core'
import { merge } from 'lodash';
import { ColorService, DEFAULT_EXCLUDE_FALSY_TOOGLE, DatesService, Dimension, DynamicDateRange, EMPTY_FALSY_ERROR, MessageService, Metric, Result, TrackingService } from '@hub-components';
import { MetricFormat } from 'app/apps/studio/app/models/dashboard.model';

import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { DEFAULT_CHART_STYLE_OPTIONS, DEFAULT_OPTIONS, DEFAULT_STYLE_OPTIONS } from '../default-chart-options';
import { resolveDashboardMetricFormat } from '../widget-metric-format.util';

const SUPPORTED_INPUTS = [{
  bindTo: 'dimensions',
  label: 'Dimension',
  type: 'dimension'
}, {
  bindTo: 'metrics',
  label: 'Metric',
  type: 'metric',
  limit: 1
}]

const HIGHCHARTS_OPTIONS = {
  chart: {
    type: 'pie'
  },
  legend: {
    enabled: true
  },
  plotOptions: {
    pie: {
      dataLabels: {
        enabled: false,
        format: '{point.percentage:.1f}%'
      },
      showInLegend: true,
      borderColor: 'transparent'
    },
    series: {
      states: {
        hover: {
          enabled: false
        }
      }
    }
  }
}

@Component({
  selector: 'pie-chart',
  template: '<app-chart *ngIf="series" [series]="series" [footer]="footer" [footerData]="footerData" [datasetDisplayName]="datasetDisplayName" [highchartsOptions]="highchartsOptions" [chartOptions]="chartOptions" [style]="style" [widgetId]="widgetId" [queryFirstDayOfWeek]="result?.query?.firstDayOfWeek"></app-chart>',
  styles: [`
    :host {
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PieChartComponent implements OnChanges {
  @Input() result: Result
  @Input() dimensions: Dimension[]
  @Input() metrics: Metric[]
  @Input() chartOptions: ChartOptions = {}
  @Input() dashboardInterval: DynamicDateRange;
  @Input() style: WidgetStyle;
  @Input() widgetId: string;
  @Input() dashboardMetricFormats: MetricFormat[] = [];

  static supportedInputs = SUPPORTED_INPUTS;
  static supportedOptions = [
    {
      bindTo: 'limit',
      label: 'Number of results',
      type: 'limitDropdown',
    },
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
        },
      ],
    },
    ...DEFAULT_OPTIONS,
    {
      bindTo: 'pieType',
      label: 'Pie type',
      type: 'pieType',
    },
    {
      bindTo: 'dataLabels',
      label: 'Data labels',
      type: 'textFormat',
      options: [
        {
          bindTo: 'rotation',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = DEFAULT_CHART_STYLE_OPTIONS;
  static supportedRollups = ['total'];
  public series: any[];
  private _series: any[] = [];
  private _filteredSeries: any[] = [];
  private excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;

  highchartsOptions: any

  datasetDisplayName: string
  footer: any;
  footerData: any;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private colorService: ColorService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private datesService: DatesService,
  ) {
    this.highchartsOptions = merge({}, HIGHCHARTS_OPTIONS)
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.result) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName
      this._series = this.getSeriesData(false);
      this._filteredSeries = this.getSeriesData(true);
      this.isFilterEmpty = this._filteredSeries.length === 0 && this._series.length !== 0;
      if (this.isFilterEmpty && this.excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateSeriesData();

      if (!this.chartOptions) {
        this.chartOptions = {}
      }

      if (this.chartOptions.dataOptions) {
        if (!this.excludeFalsy && this.isFilterEmpty && this.chartOptions.dataOptions.excludeFalsy) {
          this.messageService.info(EMPTY_FALSY_ERROR);
        }
        if (this.excludeFalsy !== this.chartOptions.dataOptions.excludeFalsy) {
          this.addTracking('excludeFalsy', changes);
          this.excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
        }
        this.updateSeriesData();
      }

      if(this.chartOptions.dataLabels && this.chartOptions.dataLabels.rotation){
        this.highchartsOptions.plotOptions.pie.dataLabels.enabled = this.chartOptions.dataLabels.rotation !== 'disabled';
      }

      if (this.chartOptions.footer) {
        if (this.footer !== undefined) {
          if (this.footer.dateRange !== this.chartOptions.footer?.dateRange) {
            this.addTracking('footerDateRange', changes);
          }
          if (this.footer.datasetInfo !== this.chartOptions.footer?.datasetInfo) {
            this.addTracking('footerDatasetInfo', changes);
          }
        }

        this.footer = this.chartOptions.footer;
        this.footerData = {
          datasetInfo: this.result.query.schema.displayName,
          dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
        };
      }

      this.series[0].innerSize = this.chartOptions.pieType === 'donut' ? '50%' : '0'

      if (this.style) {
        this.setStyle();
      }
    }
  }

  private setStyle() {
    if (this.style.enableColorPerChartValue && this.style.colorPerChartValues?.length) {
      this.chartOptions.colors = this.style.colorPerChartValues.map(colorMap => colorMap.color);
    } else if (this.style.baseColor) {
      this.chartOptions.colors = this.colorService.getSimilarColorSeries(this.style.baseColor, this.series[0].data.length)
    } else {
      this.chartOptions.colors = this.colorService.defaultColors;
    }

    this.chartOptions.textColor = this.style.textColor;
    this.chartOptions.backgroundColor = this.style.backgroundColor;
  }

  private getSeriesData(excludeFalsy: boolean): any[] {
    const dimensionIndex = this.result.dimensions.length - 1;
    const metricIndex = this.result.metrics.findIndex(m => m.name === this.metrics[0].name);
    const metricName = this.metrics[0]?.name;
    const baseFormat = this.result.metrics[metricIndex]?.format;
    const customMetricFormat = this.applyMetricFormatting(metricName, baseFormat);

    return [{
      format: customMetricFormat,
      data: this.result.rows
        .filter(r => excludeFalsy ? !!r.metrics[metricIndex].value : true)
        .map(r => {
          const slice = r.slices[dimensionIndex]
          const metric = r.metrics[metricIndex]
          const value = metric['value'] === undefined ? null : metric['value']

          return {
            name: slice.value,
            y: value,
            format: customMetricFormat,
            key: slice.value
          }
        })
    }];
  }

  private applyMetricFormatting(metricName: string, baseFormat: any) {
    const customMetricFormat = resolveDashboardMetricFormat(metricName, baseFormat, this.result?.query?.dataset, this.dashboardMetricFormats);
    if (customMetricFormat?.decimal_places === undefined) {
      return baseFormat;
    }

    this.highchartsOptions.plotOptions.pie.dataLabels.format = `{point.percentage:.${customMetricFormat.decimal_places}f}%`;
    return customMetricFormat;
  }

  private updateSeriesData(): void {
    this.series = this.excludeFalsy ? this._filteredSeries : this._series;
  }

  private addTracking(type: string, changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (type === 'excludeFalsy' && previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Pie Chart Widget: Exclude N/A and 0 values', { value: this.chartOptions.dataOptions.excludeFalsy });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (type === 'footerDateRange' && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Pie Chart Widget: Footer DateRange', { value: this.chartOptions.footer.dateRange });
        }
        if (type === 'footerDatasetInfo' && previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Pie Chart Widget: Footer Dataset Info', { value: this.chartOptions.footer.datasetInfo });
        }
      }
    }
  }
}
